/* eslint-disable no-prototype-builtins */
/* eslint-disable indent */
import { ReactNode, useCallback } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { GoTableWithSearchConfig, HeaderRule } from "./interface";
import { ReactComponent as SortUp } from "src/assets/Icons&UI/caretUpDark.svg";
import { ReactComponent as Sort } from "src/assets/Icons&UI/caretDark.svg";
import { ReactComponent as SortDisabled } from "src/assets/Icons&UI/caretPassive.svg";
import { ReactComponent as CheckboxChecked } from "src/assets/Icons&UI/checkboxChecked.svg";
import { ReactComponent as CheckboxUnchecked } from "src/assets/Icons&UI/checkboxUnchecked.svg";
import { ComponentsMap } from "./components";
import NoDataState from "../TileStates/NoDataState";
import LoadingState from "../TileStates/LoadingState";
import { useTranslation } from "react-i18next";
import { sorter } from "src/utils/sorter";
import HeaderDefaultCell from "../HeaderDefaultCell/HeaderDefaultCell";
import { HeaderMaps } from "src/config/tableMaps/HeaderMap";
import useTable from "src/hooks/useTable";

export const TableCellWithCheckbox = ({
  getTableCellBackground,
  rowData,
  index,
  handleClick,
  isItemSelected
}: {
  getTableCellBackground: any;
  rowData: any;
  index: any;
  handleClick: any;
  isItemSelected: any;
}) => {
  const onClick = useCallback(
    (event: any) => handleClick(event, rowData.uniqueId),
    [handleClick, rowData.uniqueId]
  );
  return (
    <TableCell
      sx={{
        width: "4%",
        borderRightColor: "grey.500",
        backgroundColor: getTableCellBackground(rowData, index),
        whiteSpace: "nowrap",
        textTransform: "capitalize",
        height: "56px",
        py: 0,
        textAlign: "center"
      }}
      padding="checkbox"
      onClick={onClick}
      id="checkbox-row-container"
    >
      <Checkbox
        color="primary"
        checked={isItemSelected}
        icon={<CheckboxUnchecked width={30} />}
        checkedIcon={<CheckboxChecked width={30} />}
        slotProps={{
          input: { "aria-label": "checkbox" }
        }}
        data-testid={`checkbox-${rowData.itemId}`}
        sx={{ padding: "4px" }}
      />
    </TableCell>
  );
};
const GoTableWithSelect = ({
  children,
  tableConfig
}: {
  children?: ReactNode;
  tableConfig: GoTableWithSearchConfig;
}) => {
  const {
    headerRules,
    data: tableData = [],
    apiStatus = "idle",
    sortParam = null,
    sortOrder = "ASC",
    handleChangeSort,
    onReloadClick,
    errorButtonText,
    selectedItems: selected,
    setSelectedItems: setSelected,
    isEditMode,
    page,
    rowsPerPage,
    handleChange,
    tableStyleOverrides
  } = tableConfig;
  const { t } = useTranslation();
  const { getJustifyContent, getColumnLabel, getColumnHighlight } = useTable();

  const getSortOrder = () => (sortOrder === "ASC" ? SortUp : Sort);

  const SortIconActive = getSortOrder();
  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const getSortIcon = useCallback(
    (headerItem: any, sortBy?: string) => {
      if (headerItem.isSortable) {
        const sortId = sortBy ?? headerItem.id;
        if (sortId === sortParam) {
          return <SortIconActive style={{ marginRight: "-10px" }} width={30} />;
        } else {
          return <SortDisabled style={{ marginRight: "-10px" }} width={30} />;
        }
      }
    },
    [SortIconActive, sortParam]
  );

  const handleSort = useCallback(
    (headerItem: any, sortBy?: string) => {
      if (headerItem.isSortable) {
        const sort = sortBy ?? headerItem.id;
        handleChangeSort(sort);
      }
    },
    [handleChangeSort]
  );

  const getComponent = (headerItem: any, rowData: any) => {
    if (ComponentsMap.hasOwnProperty(headerItem?.id)) {
      return ComponentsMap[headerItem?.id](headerItem, rowData);
    }
    if (
      rowData[headerItem?.id] &&
      typeof rowData[headerItem?.id] === "object"
    ) {
      return (
        <Typography
          variant="subtitle2"
          color="red"
          sx={{ textTransform: "none" }}
        >
          {t("common.error.invalidDataSupplied")}
        </Typography>
      );
    } else {
      return (
        <Typography variant="subtitle2" color="grey.900" fontWeight={"normal"}>
          {rowData[headerItem?.id] || "-"}
        </Typography>
      );
    }
  };

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelecteds = tableData.map((n) => n.uniqueId);
        setSelected(newSelecteds);
        handleChange("selectitem", newSelecteds);
        return;
      }
      setSelected([]);
      handleChange("selectitem", []);
    },
    [handleChange, setSelected, tableData]
  );

  const handleClick = useCallback(
    (_event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
      handleChange("selectitem", newSelected);
    },
    [handleChange, selected, setSelected]
  );

  const getTableCellBackground = useCallback(
    (rowData: any, vertIndex: number) => {
      if (rowData?.isRowHighlighted) {
        return "primary.contrastText";
      } else return (vertIndex % 2 !== 0 && "grey.200") || "grey.100";
    },
    []
  );

  const showPending = () => {
    return apiStatus === "pending" ? (
      <div style={{ height: "300px", width: "100%" }}>
        <LoadingState />
      </div>
    ) : null;
  };

  const showNodata = () => {
    if (
      (apiStatus !== "pending" && tableData?.length === 0) ||
      apiStatus === "error"
    ) {
      return (
        <Box sx={{ height: "300px", bgcolor: "grey.100" }}>
          <NoDataState
            OnReloadClick={apiStatus === "error" ? onReloadClick : null}
            buttonText={
              errorButtonText || (onReloadClick ? "reloadData" : "goBack")
            }
          />
        </Box>
      );
    }
    return null;
  };

  const getHeaderComponent = (headerItem: HeaderRule) => {
    if (HeaderMaps.hasOwnProperty(headerItem?.id)) {
      return HeaderMaps[headerItem?.id](headerItem, {
        getSortIcon,
        handleSort
      });
    }
    return (
      <HeaderDefaultCell
        headerItem={headerItem}
        handleSort={handleSort}
        getJustifyContent={getJustifyContent}
        getColumnLabel={getColumnLabel}
        getColumnHighlight={getColumnHighlight}
        getSortIcon={getSortIcon}
      />
    );
  };

  const sortedTableData = sorter([...tableData], sortParam, sortOrder);

  return (
    <TableContainer
      sx={{
        marginTop: "1px",
        overflowX: "auto",
        ...tableStyleOverrides
      }}
      component={Paper}
      tabIndex={0}
      aria-label="Scrollable table"
    >
      <Table aria-label="table withe search" stickyHeader>
        <TableHead>
          <TableRow>
            {isEditMode ? (
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: "grey.300",
                  width: "4%",
                  textAlign: "center"
                }}
                component="td"
              >
                <Checkbox
                  color="primary"
                  checked={
                    tableData?.length > 0 &&
                    selected?.length === tableData?.length
                  }
                  icon={<CheckboxUnchecked width={30} />}
                  checkedIcon={<CheckboxChecked width={30} />}
                  onChange={handleSelectAllClick}
                  slotProps={{
                    input: { "aria-label": "select all values" }
                  }}
                  data-testid={"checkbox-checkall"}
                />
              </TableCell>
            ) : null}
            {headerRules.map((headerItem) => (
              <TableCell
                sx={{
                  backgroundColor: "grey.300",
                  borderRight: headerItem.isSeparated ? "1px solid" : "",
                  borderRightColor: "grey.500",
                  whiteSpace: "nowrap",
                  padding: "13.5px 16px",
                  ...headerItem.headerStyleOverrides
                }}
                key={headerItem.id}
                aria-label={`${
                  /* istanbul ignore next */
                  t("tableHeaders." + headerItem.id).trim() === ""
                    ? "No value"
                    : ""
                }`}
              >
                {getHeaderComponent(headerItem)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {apiStatus !== "pending" ? (
          <TableBody data-testid="goTableWithSelect-body">
            {children}
            {sortedTableData
              ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
              ?.map((rowData: any, vertIndex: number) => {
                const isItemSelected = isSelected(rowData.uniqueId);
                return (
                  <TableRow
                    key={rowData.uniqueId ?? rowData.upc ?? rowData.itemId}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                  >
                    {isEditMode ? (
                      <TableCellWithCheckbox
                        handleClick={handleClick}
                        rowData={rowData}
                        index={vertIndex}
                        isItemSelected={isItemSelected}
                        getTableCellBackground={getTableCellBackground}
                      />
                    ) : null}
                    {headerRules.map((headerItem) => {
                      return (
                        <TableCell
                          align={headerItem.align}
                          sx={{
                            borderRight: headerItem.isSeparated
                              ? "1px solid "
                              : "",
                            borderRightColor: "grey.500",
                            backgroundColor: getTableCellBackground(
                              rowData,
                              vertIndex
                            ),
                            whiteSpace: "nowrap",
                            textTransform: "capitalize",
                            py: 0,
                            ...headerItem.dataCellStyleOverrides
                          }}
                          key={headerItem.id}
                        >
                          {getComponent(headerItem, rowData)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        ) : null}
      </Table>
      {showPending()}
      {showNodata()}
    </TableContainer>
  );
};

export default GoTableWithSelect;
